// src/components/SectionTwo.js
import React, { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { supabase } from '../supabaseClient';
import { getRecentPayments } from '../apiClient';

export default function SectionTwo({ data }) {
  const [recentPayments, setRecentPayments] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);

  // fallingBehindDrivers now comes 100% from backend summary
  const safeDrivers = Array.isArray(data?.fallingBehindDrivers)
    ? data.fallingBehindDrivers
    : [];

  const loadRecentPayments = useCallback(async () => {
    try {
      setErrorMsg('');
      setLoading(true);

      // who is logged in?
      const { data: u } = await supabase.auth.getUser();
      const uid = u?.user?.id || null;

      if (!uid) {
        setRecentPayments([]);
        setErrorMsg('Not signed in.');
        return;
      }

      // get role
      const { data: roleRow } = await supabase
        .from('approved_users')
        .select('role')
        .eq('user_id', uid)
        .maybeSingle();

      const role = roleRow?.role || 'user';

      // call backend
      const resp = await getRecentPayments(uid, role);
      if (resp.status !== 'ok') {
        setErrorMsg('Failed to load recent payments.');
        setRecentPayments([]);
        return;
      }

      setRecentPayments(resp.payments || []);
    } catch (e) {
      console.error('[SectionTwo] recent payments error:', e);
      setErrorMsg('Failed to load recent payments.');
      setRecentPayments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecentPayments().catch(() => {});
  }, [loadRecentPayments]);

  const getStatus = (daysSinceLastPayment) => {
    // You can tune these thresholds to your liking
    if (daysSinceLastPayment >= 30) return 'danger';
    if (daysSinceLastPayment >= 14) return 'warning';
    return 'success';
  };

  return (
    <div className="row g-4">
      {/* ✅ Recent Payments Table (from backend) */}
      <div className="col-md-6">
        <div className="card shadow-sm h-100">
          <div className="card-body">
            <h5 className="card-title mb-3">Recent Payments</h5>
            {errorMsg && (
              <div className="alert alert-light py-2 text-muted border">
                {errorMsg === 'Failed to load recent payments.'
                  ? 'No recent payments found or visible for your account.'
                  : errorMsg}
              </div>
            )}
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table className="table table-sm table-hover table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Driver</th>
                    <th>Plate</th>
                    <th>Date</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted">
                        Loading…
                      </td>
                    </tr>
                  ) : recentPayments.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted">
                        No recent payments
                      </td>
                    </tr>
                  ) : (
                    recentPayments.map((p) => (
                      <tr key={p.id}>
                        <td>{p.driver_name}</td>
                        <td>{p.plate_number}</td>
                        <td>{dayjs(p.payment_date).format('YYYY-MM-DD')}</td>
                        <td>{Number(p.amount || 0).toLocaleString()} TZS</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Drivers Falling Behind (from backend summary) */}
      <div className="col-md-6">
        <div className="card shadow-sm h-100">
          <div className="card-body">
            <h5 className="card-title mb-3">Drivers Falling Behind</h5>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table className="table table-sm table-hover table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Driver</th>
                    <th>Collected This Month</th>
                    <th>Last Payment</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {safeDrivers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted">
                        No drivers are behind
                      </td>
                    </tr>
                  ) : (
                    safeDrivers.map((d, index) => {
                      const days = d.daysSinceLastPayment ?? null;
                      const statusColor = getStatus(days ?? 0);
                      const lastPaid = d.lastPaymentDate || null;

                      return (
                        <tr key={index}>
                          <td>{d.name}</td>
                          <td>{Number(d.amount || 0).toLocaleString()} TZS</td>
                          <td>
                            {lastPaid
                              ? `${dayjs(lastPaid).format('YYYY-MM-DD')} (${days} days ago)`
                              : 'N/A'}
                          </td>
                          <td>
                            <span className={`badge bg-${statusColor}`}>
                              {statusColor === 'danger'
                                ? 'Critical'
                                : statusColor === 'warning'
                                ? 'Warning'
                                : 'Fair'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}